import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarClock, CheckCircle2, Loader2 } from 'lucide-react';
import api from '../services/api';

function clinicToday() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

export default function ManageFollowUp() {
  const { token } = useParams();
  const [details, setDetails] = useState(null);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState('load');
  const [error, setError] = useState('');
  const [showReschedule, setShowReschedule] = useState(false);
  const minimumDate = useMemo(clinicToday, []);

  useEffect(() => {
    let active = true;
    setBusy('load');
    api.get(`/public/follow-up/${encodeURIComponent(token)}`)
      .then(({ data }) => active && setDetails(data))
      .catch((requestError) => active && setError(requestError.response?.data?.error || 'This follow-up link could not be opened.'))
      .finally(() => active && setBusy(''));
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (!date || !details?.canReschedule || !showReschedule) { setSlots([]); return undefined; }
    let active = true;
    setBusy('slots');
    setError('');
    api.get(`/public/follow-up/${encodeURIComponent(token)}/slots`, { params: { date } })
      .then(({ data }) => active && setSlots(data?.slots || []))
      .catch((requestError) => active && setError(requestError.response?.data?.error || 'Available slots could not be loaded.'))
      .finally(() => active && setBusy(''));
    return () => { active = false; };
  }, [date, details?.canReschedule, showReschedule, token]);

  const confirm = async () => {
    setBusy('confirm'); setError('');
    try { setDetails((await api.post(`/public/follow-up/${encodeURIComponent(token)}/confirm`)).data); }
    catch (requestError) { setError(requestError.response?.data?.error || 'The follow-up could not be confirmed.'); }
    finally { setBusy(''); }
  };

  const reschedule = async (time) => {
    setBusy(`slot-${time}`); setError('');
    try {
      setDetails((await api.post(`/public/follow-up/${encodeURIComponent(token)}/reschedule`, { appointmentDate: date, time })).data);
      setDate(''); setSlots([]); setShowReschedule(false);
    } catch (requestError) { setError(requestError.response?.data?.error || 'The follow-up could not be rescheduled.'); }
    finally { setBusy(''); }
  };

  return (
    <main className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <section className="mx-auto max-w-xl rounded-3xl bg-white p-6 shadow-sm sm:p-8">
        <meta name="robots" content="noindex,nofollow" />
        <div className="flex items-center gap-3"><span className="rounded-2xl bg-teal-50 p-3 text-teal-700"><CalendarClock className="h-6 w-6" /></span><div><p className="text-xs font-bold uppercase tracking-wider text-teal-700">Dental follow-up</p><h1 className="text-2xl font-bold">Confirm or reschedule</h1></div></div>
        {error && <p role="alert" className="mt-5 rounded-xl bg-rose-50 p-4 text-sm font-semibold text-rose-800">{error}</p>}
        {busy === 'load' && <Loader2 className="mx-auto my-14 h-6 w-6 animate-spin text-teal-600" />}
        {details && <>
          <p className="mt-5 font-semibold text-teal-800">{details.clinicName}</p>
          <div className="mt-4 rounded-2xl bg-slate-50 p-5"><p className="text-sm text-slate-500">Hello {details.patientName}</p><p className="mt-1 text-lg font-bold">{details.appointmentDate}{details.appointmentTime ? ` at ${String(details.appointmentTime).slice(0, 5)}` : ''}</p></div>
          {details.confirmed ? <div role="status" className="mt-5 flex items-center gap-2 rounded-xl bg-emerald-50 p-4 font-semibold text-emerald-800"><CheckCircle2 className="h-5 w-5" />Your visit is confirmed.</div> : <button type="button" disabled={Boolean(busy)} onClick={confirm} className="mt-5 w-full rounded-xl bg-teal-700 px-5 py-3 font-bold text-white hover:bg-teal-800 disabled:opacity-60">{busy === 'confirm' ? 'Confirming...' : 'YES, I WILL COME'}</button>}
          {details.canReschedule && !showReschedule && <button type="button" disabled={Boolean(busy)} onClick={() => { setShowReschedule(true); setDate(details.appointmentDate >= minimumDate ? details.appointmentDate : minimumDate); }} className="mt-3 w-full rounded-xl border border-teal-700 px-5 py-3 font-bold text-teal-800">RESCHEDULE</button>}
          {details.canReschedule && showReschedule && <div className="mt-6 border-t pt-6"><label className="block text-sm font-bold">Choose another date<input aria-label="Reschedule date" type="date" min={minimumDate} value={date} onChange={(event) => setDate(event.target.value)} className="mt-2 w-full rounded-xl border px-4 py-3" /></label>{busy === 'slots' && <p className="mt-3 text-sm text-slate-500">Checking available slots...</p>}{date && busy !== 'slots' && slots.length === 0 && <p className="mt-3 text-sm text-slate-500">No legitimate slots are available on this date.</p>}<div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">{slots.map((slot) => <button type="button" disabled={Boolean(busy)} onClick={() => reschedule(slot.time)} key={slot.time} className="min-h-12 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 font-bold text-teal-800 hover:bg-teal-100 disabled:opacity-60">{busy === `slot-${slot.time}` ? 'Saving...' : slot.label}</button>)}</div></div>}
        </>}
      </section>
    </main>
  );
}
